import {BannerViewJs} from 'OK/banners/bannerNew/BannerViewJs';
import {DurationJs} from 'OK/banners/bannerNew/utils/DurationJs';
import {DispatcherJs} from 'OK/DispatcherJs';
import {IBannerData, BannerDataNull} from 'OK/banners/bannerNew/data/BannerDataNull';
import {BannerModelJs, RequestJs, IResponseListenerJs} from "OK/banners/bannerNew/BannerModelJs";
import {BannerDataJson} from "OK/banners/bannerNew/data/BannerDataJson";
import {ValueProviderJs} from "OK/banners/bannerNew/ValueProviderJs";
import * as logger from 'OK/logger';
import {BannerPlaceListenersProxyJs} from "OK/banners/bannerNew/BannerPlaceListenersProxyJs";

export interface IBannerJs {
    init(visible: boolean): void;
    show(): void;
    hide(): void;
    destroy(): void;
}

interface ISimpleBannerOptions {
    view: BannerViewJs | HTMLElement;
    model: BannerModelJs;
    onAfterHandleResponse?: (banner: object) => void;
}

export class SimpleBannerJs implements IBannerJs {
    options: ISimpleBannerOptions;
    model: BannerModelJs;
    view: BannerViewJs;
    callbacksEnabled = false;
    request: RequestJs | null = null;
    visible = false;
    inLayer = false;
    banner: IBannerData | null = null;
    durationSinceLastRefresh: DurationJs;
    triggers: Array<IRefreshTriggerJs>;
    listener: IResponseListenerJs;

    constructor(options: ISimpleBannerOptions) {
        let {view, model} = options;

        this.options = options;
        this.model = model;

        if (view instanceof HTMLElement) {
            view = new BannerViewJs({
                container: view,
                slot: model.slot,
                adfTransport: false
            });
        }

        this.view = view;
        this.callbacksEnabled = model.params.isCallbacksEnabled();
        this.durationSinceLastRefresh = new DurationJs();
        this.triggers = [];
        this.listener = {
            handleResponse: (banner: IBannerData) => {
                this.handleResponse(banner);
            }
        };
        if (this.callbacksEnabled) {
            BannerPlaceListenersProxyJs.getInstance().addBannerView(this.view);
        }
    }

    public setInLayer(inLayer: boolean): SimpleBannerJs {
        this.inLayer = inLayer;
        return this;
    }

    public isInViewport(): boolean {
        return this.view.isInViewport(!this.inLayer);
    }

    attachRefreshTrigger(trigger: IRefreshTriggerJs, place: string) {
        trigger.attach(this);
        this.triggers.push(new LoggingTriggerJs(trigger, place));
    }

    init(visible: boolean) {
        if (visible) {
            this.view.showContainer();
        } else {
            this.view.hideContainer();
        }
        this.triggers.forEach((trigger) => {
            trigger.init(visible);
        });
    }

    destroy() {
        this.hide();
        this.triggers.forEach((trigger) => {
            trigger.destroy();
        });
        if (this.callbacksEnabled) {
            BannerPlaceListenersProxyJs.getInstance().removeBannerView(this.view);
        }
    }

    show() {
        let step = 0;
        const stepFirst = 1;
        const stepSecond = 2;
        try {
            if (!this.visible) {
                this.visible = true;
                this.view.attach(this);
                step = stepFirst;
                this.requestBanner();
                step = stepSecond;
                this.triggers.forEach((trigger) => {
                    trigger.show();
                });
            }
        } catch (ex) {
            logger.errorEx(ex as Error, "SimpleBanner", "show_" + step);
            throw ex;
        }
    }

    hide() {
        if (this.visible) {
            this.triggers.forEach((trigger) => {
                trigger.hide();
            });
            this.visible = false;
            this.cancelBanner();
            this.view.detach();
        } else {
            this.view.renderNullBanner();
        }
    }

    protected cancelBanner() {
        if (this.request !== null) {
            this.request.cancel();
            this.request = null;
        }
    }

    requestBanner() {
        if (this.visible) {
            this.cancelBanner();
            const container = (this.view.isAdfTransport() || this.callbacksEnabled) ? this.view.getContainer() : null;
            this.request = this.model.request(container!, this.listener);
            this.durationSinceLastRefresh = new DurationJs();
        }
    }

    getDurationSinceLastRefresh(): DurationJs {
        return this.durationSinceLastRefresh;
    }

    protected handleResponse(banner: IBannerData) {
        this.banner = banner;
        this.view.renderBanner(banner);
        this.request = null;
        if (this.options.onAfterHandleResponse) {
            this.options.onAfterHandleResponse(banner);
        }
    }

    handleBannerClick() {
        if (this.banner instanceof BannerDataJson) {
            const json = this.banner;
            this.model.click(json.clickUrl);
            window.navigateOnUrlFromJS(json.targetUrl);
        }
    }
}

// =================================================
// =============== RefreshTriggerJs ================
// =================================================

export interface IRefreshTriggerJs extends IBannerJs {
    attach(simpleBanner: SimpleBannerJs): void;
}

// =================================================
// =============== LoggingTriggerJs ================
// =================================================

export class LoggingTriggerJs implements IRefreshTriggerJs {
    delegate: IRefreshTriggerJs | null;
    place: string | null;

    constructor(delegate: IRefreshTriggerJs | null = null, place: string | null = null) {
        this.delegate = delegate;
        this.place = place;
    }

    init(visible: boolean) {
        try {
            this.delegate?.init(visible);
        } catch (ex) {
            this.logAndRethrow(ex as Error, "init");
        }
    }

    show() {
        try {
            this.delegate?.show();
        } catch (ex) {
            this.logAndRethrow(ex as Error, "show");
        }
    }

    hide() {
        try {
            this.delegate?.hide();
        } catch (ex) {
            this.logAndRethrow(ex as Error, "hide");
        }
    }

    destroy() {
        try {
            this.delegate?.destroy();
        } catch (ex) {
            this.logAndRethrow(ex as Error, "destroy");
        }
    }

    attach(simpleBanner: SimpleBannerJs) {
        this.delegate?.attach(simpleBanner);
    }

    logAndRethrow(ex: Error, method: string) {
        logger.errorEx(ex, 'SimpleBanner', `${this.place}-${method}`);
        throw ex;
    }
}

// =================================================
// ============= RefreshTriggerBaseJs ==============
// =================================================

export class RefreshTriggerBaseJs extends LoggingTriggerJs {
    banner: SimpleBannerJs | null = null;

    attach(banner: SimpleBannerJs) {
        this.banner = banner;
    }

    refresh() {
        this.banner?.requestBanner();
    }

    getDurationSinceLastRefresh(): DurationJs {
        return this.banner?.getDurationSinceLastRefresh()!;
    }
}

// =================================================
// =============== TimeoutTriggerJs ================
// =================================================

export class TimeoutTriggerJs extends RefreshTriggerBaseJs {
    intervalProvider: ValueProviderJs<number>;
    refresher: number | null = null;

    constructor(intervalProvider: ValueProviderJs<number>) {
        super();
        this.intervalProvider = intervalProvider;
    }

    init(visible: boolean) {

    }

    show() {
        const interval = 1000;
        if (this.refresher === null) {
            this.refresher = setInterval(() => {
                const value = this.intervalProvider.getValue();
                if (value === null || value === 0) {
                    return;
                }
                if ((this.getDurationSinceLastRefresh().elapsedMillis() > value * interval) && this.banner?.isInViewport()) {
                    this.refresh();
                }
            }, interval);
        }
    }

    hide() {
        if (this.refresher) {
            clearInterval(this.refresher);
        }
    }

    destroy() {

    }
}

// =================================================
// ============== NavigationTriggerJs ==============
// =================================================

export class NavigationTriggerJs extends RefreshTriggerBaseJs {
    enabledProvider: ValueProviderJs<boolean>;

    constructor(enabledProvider: ValueProviderJs<boolean>) {
        super();
        this.enabledProvider = enabledProvider;
    }

    init(visible: boolean) {

    }

    show() {
        DispatcherJs.get().updateEvent.attach(this.onUpdate, this);
    }

    hide() {
        DispatcherJs.get().updateEvent.detach(this.onUpdate, this);
    }

    onUpdate() {
        if (this.enabledProvider.getValue()) {
            this.refresh();
        }
    }

    destroy() {

    }
}